import { Icon } from "@iconify/react";
import { useEffect, useState } from "react";
import type { ClassificationCategoryCandidate, parseClassificationResult } from "@/api/ai-suggestions";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { TranslationKey } from "@/locales/i18n";
import { useI18n } from "@/locales/i18n-provider";
import { cn } from "@/utils";

const AI_PROGRESS_MESSAGE_KEYS = [
  "tickets.ai.progress.reading",
  "tickets.ai.progress.analyzing",
  "tickets.ai.progress.matching",
  "tickets.ai.progress.evaluating",
] as const satisfies ReadonlyArray<TranslationKey>;

export type AppliedAiCategoryFeedback = {
  confidence: number;
} | null;

type AiCategoryLabelActionProps = {
  appliedFeedback: AppliedAiCategoryFeedback;
  canGenerate: boolean;
  isApplying: boolean;
  isGenerating: boolean;
  isLoading: boolean;
  progressMessage: string;
  onGenerate: () => void;
};

type TicketAiClassificationProps = {
  canApply: boolean;
  canGenerate: boolean;
  categoryById: Map<string, { path: string }>;
  classification: ReturnType<typeof parseClassificationResult>;
  currentCategoryId: string | null;
  hasCategory: boolean;
  isApplying: boolean;
  isGenerating: boolean;
  isLoading: boolean;
  latestGeneratedSuggestionId: string | null;
  progressMessage: string;
  suggestionId: string | null;
  applyingCategoryId: string | null;
  onApply: (candidate: ClassificationCategoryCandidate) => void;
  onGenerate: () => void;
};

export function useAiProgressMessage(isGenerating: boolean) {
  const { t } = useI18n();
  const [step, setStep] = useState(0);

  useEffect(() => {
    setStep(0);
    if (!isGenerating) return;

    const interval = window.setInterval(() => {
      setStep((current) => Math.min(current + 1, AI_PROGRESS_MESSAGE_KEYS.length - 1));
    }, 1800);

    return () => window.clearInterval(interval);
  }, [isGenerating]);

  return t(AI_PROGRESS_MESSAGE_KEYS[step] ?? AI_PROGRESS_MESSAGE_KEYS[0]);
}

/** 已有分类时，AI 入口退到分类标签右侧；生成进度也在原位切换，避免布局跳动。 */
export function AiCategoryLabelAction({
  appliedFeedback,
  canGenerate,
  isApplying,
  isGenerating,
  isLoading,
  progressMessage,
  onGenerate,
}: AiCategoryLabelActionProps) {
  const { intlLocale, t } = useI18n();

  if (isGenerating) {
    return (
      <span className="flex max-w-52 items-center justify-end gap-1 text-[11px] text-primary" title={progressMessage}>
        <Icon aria-hidden="true" className="size-3.5 shrink-0 animate-pulse" icon="tabler:sparkles" />
        <span aria-hidden="true" className="truncate">
          {progressMessage}
        </span>
        <span aria-live="polite" className="sr-only">
          {t("tickets.ai.generatingAria")}
        </span>
      </span>
    );
  }

  if (isApplying) {
    return (
      <span aria-live="polite" className="text-[11px] text-primary">
        {t("tickets.ai.applying")}
      </span>
    );
  }

  if (appliedFeedback) {
    return (
      <span aria-live="polite" className="flex items-center gap-1 text-[11px] text-primary">
        <Icon aria-hidden="true" className="size-3.5" icon="tabler:circle-check" />
        {t("tickets.ai.applied", { confidence: formatConfidence(appliedFeedback.confidence, intlLocale) })}
      </span>
    );
  }

  if (!canGenerate) return null;

  return (
    <Button
      aria-label={t("tickets.ai.reviewAria")}
      className="h-5 px-1.5 text-[11px] font-normal text-muted-foreground hover:bg-transparent hover:text-primary"
      disabled={isLoading}
      size="xs"
      type="button"
      variant="ghost"
      onClick={onGenerate}
    >
      <Icon aria-hidden="true" icon="tabler:sparkles" />
      {t("tickets.ai.review")}
    </Button>
  );
}

/** 未分类时保留轻量生成入口；候选只在确实有结果时展开。 */
export function TicketAiClassification({
  applyingCategoryId,
  canApply,
  canGenerate,
  categoryById,
  classification,
  currentCategoryId,
  hasCategory,
  isApplying,
  isGenerating,
  isLoading,
  latestGeneratedSuggestionId,
  progressMessage,
  suggestionId,
  onApply,
  onGenerate,
}: TicketAiClassificationProps) {
  const { intlLocale, t } = useI18n();
  const [showAlternatives, setShowAlternatives] = useState(false);
  const [visibleMatchSuggestionId, setVisibleMatchSuggestionId] = useState<string | null>(null);
  const candidates = classification?.categories.slice(0, 3) ?? [];
  const bestCandidate = candidates[0];
  const matchesCurrentCategory = Boolean(hasCategory && bestCandidate && bestCandidate.categoryId === currentCategoryId);
  const isFreshMatchingResult = Boolean(suggestionId && suggestionId === latestGeneratedSuggestionId && matchesCurrentCategory);

  useEffect(() => {
    if (!suggestionId || !isFreshMatchingResult) return;

    setVisibleMatchSuggestionId(suggestionId);
    const timeout = window.setTimeout(() => setVisibleMatchSuggestionId(null), 3200);
    return () => window.clearTimeout(timeout);
  }, [isFreshMatchingResult, suggestionId]);

  if (isGenerating) {
    if (hasCategory) return null;
    return <AiProgressLine message={progressMessage} />;
  }

  if (hasCategory && visibleMatchSuggestionId === suggestionId && bestCandidate) {
    return (
      <p aria-live="polite" className="mt-1 flex min-h-8 items-center gap-1.5 text-xs text-primary">
        <Icon aria-hidden="true" className="size-3.5" icon="tabler:circle-check" />
        {t("tickets.ai.currentReasonable", { confidence: formatConfidence(bestCandidate.confidence, intlLocale) })}
      </p>
    );
  }

  if (bestCandidate && !matchesCurrentCategory) {
    return (
      <div className="mt-1 space-y-2.5">
        <SuggestionRow
          applyingCategoryId={applyingCategoryId}
          canApply={canApply}
          candidate={bestCandidate}
          categoryById={categoryById}
          isApplying={isApplying}
          isBest
          onApply={onApply}
        />

        {candidates.length > 1 ? (
          <>
            <Button
              aria-expanded={showAlternatives}
              className="h-6 px-1.5 text-xs font-normal text-muted-foreground hover:bg-transparent hover:text-foreground"
              size="xs"
              type="button"
              variant="ghost"
              onClick={() => setShowAlternatives((current) => !current)}
            >
              {t("tickets.ai.otherSuggestions", { count: new Intl.NumberFormat(intlLocale).format(candidates.length - 1) })}
              <Icon aria-hidden="true" icon={showAlternatives ? "tabler:chevron-up" : "tabler:chevron-down"} />
            </Button>
            {showAlternatives ? (
              <div className="space-y-2">
                {candidates.slice(1).map((candidate) => (
                  <SuggestionRow
                    applyingCategoryId={applyingCategoryId}
                    canApply={canApply}
                    candidate={candidate}
                    categoryById={categoryById}
                    isApplying={isApplying}
                    key={candidate.categoryId}
                    onApply={onApply}
                  />
                ))}
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    );
  }

  if (hasCategory) return null;

  if (classification && candidates.length === 0) {
    return (
      <div className="mt-1 flex min-h-8 items-center justify-between gap-3 text-xs text-muted-foreground">
        <span>{t("tickets.ai.noMatch")}</span>
        {canGenerate ? (
          <Button className="px-1.5 text-primary" size="xs" type="button" variant="ghost" onClick={onGenerate}>
            {t("tickets.ai.regenerate")}
          </Button>
        ) : null}
      </div>
    );
  }

  if (!canGenerate) return null;

  return (
    <div className="mt-1 flex min-h-8 items-center justify-between gap-3">
      <span className="flex items-center gap-1.5 text-xs font-medium text-primary">
        <Icon aria-hidden="true" className="size-3.5" icon="tabler:sparkles" />
        {t("tickets.ai.classification")}
      </span>
      <Button className="px-1.5 text-primary" disabled={isLoading} size="xs" type="button" variant="ghost" onClick={onGenerate}>
        {t("tickets.ai.generate")}
      </Button>
    </div>
  );
}

function AiProgressLine({ message }: { message: string }) {
  const { t } = useI18n();

  return (
    <p className="mt-1 flex min-h-8 items-center gap-1.5 text-xs text-primary" title={message}>
      <Icon aria-hidden="true" className="size-3.5 animate-pulse" icon="tabler:sparkles" />
      <span aria-hidden="true" className="truncate">
        {message}
      </span>
      <span aria-live="polite" className="sr-only">
        {t("tickets.ai.generatingAria")}
      </span>
    </p>
  );
}

type SuggestionRowProps = {
  applyingCategoryId: string | null;
  canApply: boolean;
  candidate: ClassificationCategoryCandidate;
  categoryById: Map<string, { path: string }>;
  isApplying: boolean;
  isBest?: boolean;
  onApply: (candidate: ClassificationCategoryCandidate) => void;
};

function SuggestionRow({ applyingCategoryId, canApply, candidate, categoryById, isApplying, isBest = false, onApply }: SuggestionRowProps) {
  const { intlLocale, t } = useI18n();
  const categoryLabel = categoryById.get(candidate.categoryId)?.path ?? t("tickets.ai.categoryFallback", { id: candidate.categoryId.slice(0, 8) });
  const isCurrentApplyingCandidate = isApplying && applyingCategoryId === candidate.categoryId;
  const reason = candidate.reason ?? t(isBest ? "tickets.ai.bestReasonFallback" : "tickets.ai.alternativeReasonFallback");

  return (
    <div className={cn("rounded-lg px-3 py-2.5", isBest ? "bg-accent/70" : "bg-muted/70")}>
      <div className="flex items-start gap-2.5">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className={cn("text-xs font-semibold tabular-nums", isBest ? "text-primary" : "text-muted-foreground")}>
              {formatConfidence(candidate.confidence, intlLocale)}
            </span>
            {isBest ? (
              <Badge className="h-4 px-1.5 text-[10px]" variant="secondary">
                {t("tickets.ai.bestMatch")}
              </Badge>
            ) : null}
          </div>
          <p className="truncate text-sm font-medium" title={categoryLabel}>
            {categoryLabel}
          </p>
          <p className="line-clamp-2 text-[11px] leading-4 text-muted-foreground">{reason}</p>
        </div>
        {canApply ? (
          <Button
            aria-busy={isCurrentApplyingCandidate}
            aria-label={t(isCurrentApplyingCandidate ? "tickets.ai.applyingCategoryAria" : "tickets.ai.applyCategoryAria", { category: categoryLabel })}
            className="mt-1"
            disabled={isApplying}
            size="xs"
            type="button"
            variant={isBest ? "default" : "secondary"}
            onClick={() => onApply(candidate)}
          >
            {t(isCurrentApplyingCandidate ? "tickets.ai.applyingShort" : "tickets.ai.apply")}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function formatConfidence(confidence: number, intlLocale: string): string {
  return new Intl.NumberFormat(intlLocale, { style: "percent", maximumFractionDigits: 0 }).format(confidence);
}
